import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { enUS, es } from 'date-fns/locale';
import { endOfDay, endOfWeek, isAfter, isBefore, isSameDay, parseISO, startOfDay, startOfWeek } from 'date-fns';
import { Box, CircularProgress, IconButton, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  CalendarMonth as CalendarIcon, PendingActions as PendingIcon, CheckCircle as CheckIcon,
  EventBusy as AbsentIcon, Close as CloseIcon
} from '@mui/icons-material';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import ConfirmDeleteModal from 'components/shared/modals/ConfirmDeleteModal';
import SessionDetailDrawer from 'components/session/SessionDetailDrawer';
import SessionFilters from 'components/session/SessionFilters';
import SessionListItem from 'components/session/SessionListItem';
import SessionDrawerListItem from 'components/session/SessionDrawerListItem';
import SessionCounters from 'components/session/SessionCounters';
import GroupAttendancePanel from 'components/session/GroupAttendancePanel';
import PillTabs from 'components/shared/tabs/PillTabs';
import { timeFormat } from 'common/constants/date';
import { convertUtcToLocalTime } from 'common/utils/date';
import { normalizeSessionStatus } from 'common/utils/sessionColor';
import AppBarContext from 'context/AppBarContext';
import groupSessionSlice from 'store/modules/groupSession';

const INITIAL_FILTERS = { category: '', trainerId: '', status: '', timeRange: '', date: {} };
const TABS = ['today', 'week', 'upcoming', 'history'];
const rangeLabel = (s) => `${convertUtcToLocalTime(s.start_date, timeFormat)} – ${convertUtcToLocalTime(s.end_date, timeFormat)}`;

const tabMatches = (tab, date, now, weekStart, weekEnd) => {
  switch (tab) {
    case 'today': return isSameDay(date, now);
    case 'week': return !isBefore(date, weekStart) && !isAfter(date, weekEnd);
    case 'upcoming': return isAfter(date, weekEnd);
    case 'history': return isBefore(date, startOfDay(now));
    default: return true;
  }
};

function GroupSessionsView({ trainerScoped = false }) {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { t, i18n } = useTranslation(['labels', 'options', 'routes', 'sessions', 'tables', 'buttons']);
  const dateLocale = i18n.language === 'es' ? es : enUS;
  const { setAppBarButton, setAppBarTitle } = useContext(AppBarContext);
  const pageTitle = t('session.groupSessions', { ns: 'routes' });

  const userId = useSelector((state) => state.auth.user.id);
  const sessions = useSelector((state) => state.groupSession.data.list);
  const storeStatus = useSelector((state) => state.groupSession.status.general);
  const refresh = useSelector((state) => state.groupSession.refresh);
  const selectedSessionId = useSelector((state) => state.groupSession.selectedSessionId);
  const detailMode = useSelector((state) => state.groupSession.detailMode);
  const listFilter = useSelector((state) => state.groupSession.listFilter);
  const cameFromList = useSelector((state) => state.groupSession.cameFromList);

  // El toggle trabaja por índice (PillTabs), pero el filtrado sigue leyendo la clave de TABS.
  const [tabIndex, setTabIndex] = useState(TABS.indexOf('week'));
  const tab = TABS[tabIndex];
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null });

  useEffect(() => {
    setAppBarButton(null);
    setAppBarTitle(pageTitle);
  }, [pageTitle, setAppBarButton, setAppBarTitle]);

  useEffect(() => {
    if (trainerScoped && !userId) return undefined;
    const payload = { pagination: { first: 100, page: 1 } };
    if (trainerScoped) payload.filters = { trainer_id: [{ value: parseInt(userId, 10) }] };
    dispatch(groupSessionSlice.actions.list(payload));
    return () => { dispatch(groupSessionSlice.actions.clearSelection()); };
  }, [dispatch, refresh, userId, trainerScoped]);

  const now = new Date();
  const weekStart = startOfWeek(now, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(now, { weekStartsOn: 1 });

  const allItems = useMemo(() => sessions
    .map((s) => ({ session: s, norm: normalizeSessionStatus(s.status, s.start_date), hasAbsence: (s.attendances ?? []).some((a) => a.status === 'ABSENT'), date: new Date(s.start_date) }))
    .sort((a, b) => a.date - b.date), [sessions]);

  const tabItems = useMemo(() => allItems.filter((it) => tabMatches(tab, it.date, now, weekStart, weekEnd)), [allItems, tab, now, weekStart, weekEnd]);

  const counts = useMemo(() => tabItems.reduce((acc, it) => {
    if (it.norm === 'scheduled') acc.scheduled += 1;
    else if (it.norm === 'pending') acc.pending += 1;
    else if (it.norm === 'completed') acc.completed += 1;
    if (it.hasAbsence || it.norm === 'absent') acc.absent += 1;
    return acc;
  }, { scheduled: 0, pending: 0, completed: 0, absent: 0 }), [tabItems]);

  const categoryOptions = useMemo(() => {
    const seen = new Set();
    const opts = [{ value: 'all', label: t('allCategories', { ns: 'sessions', defaultValue: 'Todas las categorías' }) }];
    allItems.forEach(({ session: s }) => {
      const c = s.session_type_id;
      if (c && !seen.has(c.id)) { seen.add(c.id); opts.push({ value: c.id, label: c.name }); }
    });
    return opts;
  }, [allItems, t]);

  const timeRangeOptions = useMemo(() => {
    const set = new Set();
    allItems.forEach(({ session: s }) => set.add(rangeLabel(s)));
    return [...set].sort();
  }, [allItems]);

  // Entrenadores presentes en las sesiones cargadas (match por nombre, como en la vista del cliente).
  const trainerOptions = useMemo(() => {
    const seen = new Set();
    const opts = [];
    allItems.forEach(({ session: s }) => {
      const name = s.trainer?.full_name;
      if (name && !seen.has(name)) { seen.add(name); opts.push({ value: name, label: name }); }
    });
    return opts;
  }, [allItems]);

  const statusOptions = [
    { value: 'all', label: t('allStatuses', { ns: 'sessions', defaultValue: 'Todos los estados' }) },
    { value: 'scheduled', label: t('statusScheduledPlural', { ns: 'sessions', defaultValue: 'Programadas' }) },
    { value: 'pending', label: t('statusPendingPlural', { ns: 'sessions', defaultValue: 'Pendientes por reportar' }) },
    { value: 'completed', label: t('statusCompletedPlural', { ns: 'sessions', defaultValue: 'Completadas' }) },
    { value: 'absent', label: t('absences', { ns: 'sessions', defaultValue: 'Ausencias' }) }
  ];

  const filteredItems = useMemo(() => tabItems.filter(({ session: s, norm, hasAbsence }) => {
    if (filters.category && s.session_type_id?.id !== filters.category) return false;
    if (filters.trainerId && s.trainer?.full_name !== filters.trainerId) return false;
    if (filters.timeRange && rangeLabel(s) !== filters.timeRange) return false;
    if (filters.status) {
      if (filters.status === 'absent') { if (!hasAbsence && norm !== 'absent') return false; }
      else if (norm !== filters.status) return false;
    }
    if (filters.date?.start_date && isBefore(parseISO(s.start_date), startOfDay(parseISO(filters.date.start_date)))) return false;
    if (filters.date?.end_date && isAfter(parseISO(s.start_date), endOfDay(parseISO(filters.date.end_date)))) return false;
    return true;
  }), [tabItems, filters]);

  const counterListItems = useMemo(() => {
    if (!listFilter) return [];
    return filteredItems.filter(({ norm, hasAbsence }) => (listFilter === 'absent' ? (hasAbsence || norm === 'absent') : norm === listFilter));
  }, [filteredItems, listFilter]);

  const selectedItem = useMemo(() => allItems.find((it) => String(it.session.id) === String(selectedSessionId)), [allItems, selectedSessionId]);

  const openDetail = useCallback((id) => dispatch(groupSessionSlice.actions.selectSession(id)), [dispatch]);
  const closePanel = useCallback(() => dispatch(groupSessionSlice.actions.clearSelection()), [dispatch]);
  const openCounterList = useCallback((key) => dispatch(groupSessionSlice.actions.showCounterList(key)), [dispatch]);
  const backToList = useCallback(() => dispatch(groupSessionSlice.actions.backToList()), [dispatch]);

  const confirmDelete = useCallback(() => {
    if (deleteModal.id) dispatch(groupSessionSlice.actions.deleteSession([deleteModal.id]));
    setDeleteModal({ open: false, id: null });
    closePanel();
  }, [deleteModal.id, dispatch, closePanel]);

  const subByTab = {
    today: t('today', { ns: 'sessions', defaultValue: 'Hoy' }),
    week: t('thisWeek', { ns: 'sessions', defaultValue: 'Esta semana' }),
    upcoming: t('upcomingTab', { ns: 'sessions', defaultValue: 'Próximas' }),
    history: t('historyTab', { ns: 'sessions', defaultValue: 'Historial' })
  };
  const sub = subByTab[tab];

  const counters = [
    { key: 'scheduled', label: t('statusScheduledPlural', { ns: 'sessions', defaultValue: 'Programadas' }), value: counts.scheduled, sub, color: theme.palette.sessionProximity.far, icon: <CalendarIcon /> },
    { key: 'pending', label: t('pendingShort', { ns: 'sessions', defaultValue: 'Pendientes por reportar' }), value: counts.pending, sub: t('requireReport', { ns: 'sessions', defaultValue: 'Requieren tu reporte' }), color: theme.palette.sessionStatus.pending.fg, icon: <PendingIcon /> },
    { key: 'completed', label: t('statusCompletedPlural', { ns: 'sessions', defaultValue: 'Completadas' }), value: counts.completed, sub, color: theme.palette.sessionStatus.completed.fg, icon: <CheckIcon /> },
    { key: 'absent', label: t('absences', { ns: 'sessions', defaultValue: 'Ausencias' }), value: counts.absent, sub, color: theme.palette.sessionStatus.absent.fg, icon: <AbsentIcon /> }
  ];

  const isLoading = storeStatus === 'loading' && sessions.length === 0;
  const panelOpen = detailMode === 'detail' ? Boolean(selectedItem) : detailMode === 'list';

  let drawerContent = <Box />;
  if (detailMode === 'detail' && selectedItem) {
    drawerContent = <GroupAttendancePanel session={selectedItem.session} onClose={closePanel} onBack={cameFromList ? backToList : null} />;
  } else if (detailMode === 'list') {
    drawerContent = (
      <Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>{counters.find((c) => c.key === listFilter)?.label}</Typography>
          <IconButton size="small" onClick={closePanel} sx={{ color: 'text.secondary' }}><CloseIcon fontSize="small" /></IconButton>
        </Stack>
        <Stack spacing={1.25}>
          {counterListItems.length === 0
            ? <Typography variant="body2" color="text.secondary">{t('empty', { type: pageTitle.toLowerCase(), ns: 'tables' })}</Typography>
            : counterListItems.map(({ session: s }) => (
              <SessionDrawerListItem key={s.id} session={s} dateLocale={dateLocale} onClick={() => openDetail(s.id)} />
            ))}
        </Stack>
      </Box>
    );
  }

  return (
    <>
      <SEOPageTitle title={pageTitle} />
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        {t('groupSessionsSubtitle', { ns: 'sessions', defaultValue: 'Gestiona tus sesiones, revisa asistencias y reporta resultados.' })}
      </Typography>

      <Box sx={{ mb: 3 }}>
        <SessionCounters counters={counters} activeKey={detailMode === 'list' ? listFilter : null} onSelect={openCounterList} />
      </Box>

      <PillTabs
        value={tabIndex}
        onChange={(_, v) => setTabIndex(v)}
        ariaLabel={pageTitle}
        sx={{ mb: 2 }}
        tabs={TABS.map((tk) => ({ key: tk, label: subByTab[tk] }))}
      />

      <SessionFilters categoryOptions={categoryOptions} trainerOptions={!trainerScoped ? trainerOptions : null} timeRangeOptions={timeRangeOptions} statusOptions={statusOptions} onApply={setFilters} />

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} sx={{ color: 'secondary.main' }} /></Box>
      ) : (
        <Stack spacing={1.5} sx={{ mt: 2 }}>
          {filteredItems.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography variant="body2" color="text.secondary">{t('empty', { type: pageTitle.toLowerCase(), ns: 'tables' })}</Typography>
            </Box>
          ) : filteredItems.map(({ session: s, norm }) => (
            <SessionListItem
              key={s.id}
              session={s}
              dateLocale={dateLocale}
              selected={String(s.id) === String(selectedSessionId)}
              actionLabel={norm === 'completed' ? t('viewDetail', { ns: 'sessions', defaultValue: 'Ver detalle' }) : t('takeAttendance', { ns: 'sessions', defaultValue: 'Tomar asistencia' })}
              onAction={() => openDetail(s.id)}
              onDelete={!trainerScoped ? () => setDeleteModal({ open: true, id: s.id }) : null}
            />
          ))}
        </Stack>
      )}

      <SessionDetailDrawer open={panelOpen} onClose={closePanel}>{drawerContent}</SessionDetailDrawer>
      <ConfirmDeleteModal open={deleteModal.open} onClose={() => setDeleteModal({ open: false, id: null })} onConfirm={confirmDelete} count={1} />
    </>
  );
}

GroupSessionsView.propTypes = { trainerScoped: PropTypes.bool };

export default GroupSessionsView;
